import { useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { fetchChatHistoryByUser, projectCreated, projectRenamed, projectDeleted } from '../slice/api/historySlice'
import { fetchTasksAsync } from '../slice/taskSlice'
import { fetchDepartments } from '../slice/departmentsSlice'
import { compareAgentOrder } from '../utils/agentOrder'
import { fetchSQByTaskId } from '../slice/sqSlice'
import { getTaskStatusesByHistoryAsync } from '../slice/taskStatusSlice'
import { fetchAgentLayout } from '../slice/agentWidgetsSlice'
import AnswerStatusBadges from '../components/atomic/AnswerStatusBadges'
import { useTranslation } from '../i18n/LanguageContext'
import { useThemeColor } from '../hooks/useThemeColor'
import { parseISO, format } from 'date-fns'
import { Plus, MoreVertical, Eye, Pencil, Trash2, X, ChevronDown, Check, Upload, FolderOpen, FileText, Image as ImageIcon, AlertCircle, BrainCircuit, Share2 } from 'lucide-react'
import { deleteHistory, updateHistoryName, createChatHistory, uploadFilesWithHistory } from '../Api/req'
import axios from 'axios'
import { v4 as uuidv4 } from 'uuid'
import toast, { Toaster } from 'react-hot-toast'
import * as pdfjsLib from 'pdfjs-dist'
import SharePointFileBrowser from './settings/components/SharePointFileBrowser'
import ShareModal from '../components/ShareModal'
import CustomImageField from './settings/components/CustomImageField'
import CustomFileField from './settings/components/CustomFileField'
import { BASE_URL, BASE_SHARES_URL } from '../components/constants'
import { clearSyncResults } from '../slice/sharepointSlice'
import { authHeader, authHeaders } from '../utils/authHeaders';

// Ensure pdfjs worker is set up for counting pages
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`

// Agent Icon Component — solid app theme color with sidebar's agent icon
const AgentIcon = () => {
  const { aispaceColor } = useThemeColor();
  return (
    <div
      className="w-[52px] h-[52px] rounded-3xl flex items-center justify-center flex-shrink-0"
      style={{ backgroundColor: `${aispaceColor}20` }}
    >
      <BrainCircuit className="w-7 h-7" strokeWidth={2} style={{ color: aispaceColor }} />
    </div>
  );
};

// Agent Card Component
// Inline image renderer for AgentCard — mirrors the AgentImage pattern used
// in pages/Agents.jsx: when the admin has uploaded an image (truthy
// agent.image_blob_path) we fetch it from /agents/media/:id; on missing
// image OR a load error we fall back to the default AgentIcon. Local
// component so the fallback toggle is self-contained per card.
const AgentCardImage = ({ agentId, agentName, imagePath }) => {
  const [imgError, setImgError] = useState(false);
  const showImage = imagePath && !imgError;
  if (!showImage) return <AgentIcon />;
  return (
    <img
      src={`${BASE_URL}/agents/media/${agentId}`}
      alt={agentName || ''}
      className="w-12 h-12 rounded-xl object-cover flex-shrink-0"
      onError={() => setImgError(true)}
    />
  );
};

const AgentCard = ({ agent, themeColor, onClick }) => {
  return (
    <div
      onClick={onClick}
      className="flex items-start gap-4 p-5 bg-[#F5F5F5] rounded-2xl hover:bg-[#EEEEEE] transition-colors cursor-pointer outline outline-1 outline-[#EEEEEE]"
    >
      <AgentCardImage
        agentId={agent.id}
        agentName={agent.name}
        imagePath={agent.image_blob_path}
      />
      <div className="flex-1 min-w-0 pt-1">
        <h3 className="font-semibold text-[15px] mb-1.5 leading-tight text-black">
          {agent.name}
        </h3>
        <p className="text-[13px] text-black leading-[1.6]">{agent.description}</p>
      </div>
    </div>
  );
};

// Project Icon Component — solid app theme color so every project bubble
// follows the active aispace color instead of the previous hardcoded yellow.
// Pulls the color from useThemeColor() directly so callers don't need to
// drill it through props.
const ProjectIcon = () => {
  const { aispaceColor } = useThemeColor();
  return (
    <div
      className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
      style={{ backgroundColor: `${aispaceColor}20`, color: aispaceColor }}
    >
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ color: aispaceColor }}>
        <path d="M12 4L4 8L12 12L20 8L12 4Z" fill="currentColor" fillOpacity="0.9"/>
        <path d="M4 12L12 16L20 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" strokeOpacity="0.9"/>
        <path d="M4 16L12 20L20 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" strokeOpacity="0.7"/>
      </svg>
    </div>
  );
};

// Project image component — matches AgentImage pattern. The fallback banner
// also uses the live theme color so neither surface falls back to the old
// yellow gradient.
const ProjectImage = ({ projectId, imagePath, type = 'icon' }) => {
  const [imgError, setImgError] = useState(false);
  const { aispaceColor } = useThemeColor();
  const showImage = imagePath && !imgError;
  const imgSrc = imagePath ? `${BASE_URL}/projects/image/${projectId}` : '';

  if (type === 'banner') {
    return (
      <div className="w-full h-40">
        {showImage ? (
          <img src={imgSrc} alt="" className="w-full h-full object-cover" onError={() => setImgError(true)} />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center"
            style={{ backgroundColor: aispaceColor }}
          >
            <svg className="w-12 h-12 text-white opacity-80" viewBox="0 0 24 24" fill="none">
              <path d="M12 4L4 8L12 12L20 8L12 4Z" fill="currentColor" fillOpacity="0.9"/>
              <path d="M4 12L12 16L20 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M4 16L12 20L20 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" strokeOpacity="0.7"/>
            </svg>
          </div>
        )}
      </div>
    );
  }

  // icon type (list view)
  return showImage ? (
    <img src={imgSrc} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0" onError={() => setImgError(true)} />
  ) : (
    <ProjectIcon />
  );
};

const HomeScreen = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { aispaceColor } = useThemeColor();
  const { userHistories: chatHistory = [], userHistoriesStatus: status } = useSelector((state) => state.history);
  const { tasks = [], status: tasksStatus } = useSelector((state) => state.tasks);
  const { data: userData } = useSelector((state) => state.admin);
  const { departments: reduxDepartments = [] } = useSelector((state) => state.departments);
  const { syncResults } = useSelector((state) => state.sharepoint);
  const { showProjectDetailByTask, overviewEnabledByTask, layoutLoadingByTask, layoutByTask } = useSelector((state) => state.agentWidgets);
  const [activeDepartment, setActiveDepartment] = useState('');
  const [showAllAgents, setShowAllAgents] = useState(false);
  const [historyData, setHistoryData] = useState(new Map());
  const [openDropdown, setOpenDropdown] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, openUp: false });
  const [editingProject, setEditingProject] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, project: null });
  const [shareTarget, setShareTarget] = useState(null);
  const [sharedHistoryIds, setSharedHistoryIds] = useState(new Set());
  const [createProjectModal, setCreateProjectModal] = useState(false);
  const [showSharePointBrowser, setShowSharePointBrowser] = useState(false);
  const [importedSharePointFiles, setImportedSharePointFiles] = useState([]);
  const [sharepointCustomFieldId, setSharepointCustomFieldId] = useState(null);
  const [newProject, setNewProject] = useState({
    agent: '',
    projectName: '',
    projectPicture: null,
    uploadMethod: '', // 'device' or 'sharepoint' - empty by default
    selectedFolders: [],
    uploadedFiles: [],
    selectedSharePointFiles: [],
    sharePointConfigured: false
  });
  const [isCreatingProject, setIsCreatingProject] = useState(false);
  const [isStartingDirectChat, setIsStartingDirectChat] = useState(false);
  const [isAgentPreSelected, setIsAgentPreSelected] = useState(false); // Track if agent was selected from card click
  const [agentDropdownOpen, setAgentDropdownOpen] = useState(false);
  const agentDropdownRef = useRef(null);
  const agentTriggerRef = useRef(null);
  const [agentMenuPos, setAgentMenuPos] = useState(null);

  // Custom fields for the currently selected agent (from Agent Settings → Project Setup)
  const [customFields, setCustomFields] = useState([]);
  const [customFieldValues, setCustomFieldValues] = useState({});
  const [customFieldsLoading, setCustomFieldsLoading] = useState(false);
  const [customFieldErrors, setCustomFieldErrors] = useState({});

  useEffect(() => {
    const onDocClick = (e) => {
      if (agentDropdownRef.current && !agentDropdownRef.current.contains(e.target)) setAgentDropdownOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [])

  const fetchMyShares = () => {
    const token = localStorage.getItem('token')
    fetch(`${BASE_SHARES_URL}/my-shares`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : { history_ids: [] })
      .then(d => setSharedHistoryIds(new Set((d.history_ids || []).map(String))))
      .catch(() => {})
  }
  useEffect(() => { fetchMyShares() }, [])

  // Fetch custom Project Setup fields for the selected agent. Initialise the
  // form with default values and clear any prior validation errors.
  useEffect(() => {
    if (!createProjectModal || !newProject.agent) {
      setCustomFields([]);
      setCustomFieldValues({});
      setCustomFieldErrors({});
      return;
    }
    let cancelled = false;
    // Clear stale custom fields from the previously selected agent so they
    // don't flash before the new schema arrives.   
    setCustomFields([]);
    setCustomFieldValues({});
    setCustomFieldErrors({});
    setCustomFieldsLoading(true);
    axios
      .get(`${BASE_URL}/agent-custom-fields/task/${newProject.agent}`, authHeaders())
      .then((res) => {
        if (cancelled) return;
        const list = res.data?.fields || [];
        setCustomFields(list);
        const initial = {};
        list.forEach((f) => {
          initial[f.id] = (f.field_type === 'image' || f.field_type === 'file')
            ? []
            : (f.default_value || '');
        });
        setCustomFieldValues(initial);
        setCustomFieldErrors({});
      })
      .catch(() => {
        if (cancelled) return;
        setCustomFields([]);
        setCustomFieldValues({});
        setCustomFieldErrors({});
      })
      .finally(() => { if (!cancelled) setCustomFieldsLoading(false); });
    return () => { cancelled = true; };
  }, [createProjectModal, newProject.agent])
  const projectPictureRef = useRef(null);
  const documentInputRef = useRef(null);
  const dropdownRef = useRef(null);

  const user_id = localStorage.getItem('id');
  const email = localStorage.getItem('email');

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Open delete confirmation modal
  const handleDeleteClick = (project) => {
    setDeleteModal({ isOpen: true, project });
    setOpenDropdown(null);
  };

  // Handle project delete after confirmation
  const confirmDelete = async () => {
    if (!deleteModal.project) return;
    try {
      setDeletingId(deleteModal.project.id);
      await deleteHistory(deleteModal.project.id);
      dispatch(projectDeleted(deleteModal.project.id));
      setDeleteModal({ isOpen: false, project: null });
    } catch (error) {
      console.error('Failed to delete project:', error);
    } finally {
      setDeletingId(null);
    }
  };

  // Cancel delete
  const cancelDelete = () => {
    setDeleteModal({ isOpen: false, project: null });
  };

  // Create Project Modal functions
  const openCreateProjectModal = () => {
    setCreateProjectModal(true);
    setIsAgentPreSelected(false); // Allow agent selection when opened from button
    setImportedSharePointFiles([]);
    setSharepointCustomFieldId(null);
    setNewProject({
      agent: '',
      projectName: '',
      projectPicture: null,
      uploadMethod: '',
      selectedFolders: [],
      uploadedFiles: [],
      selectedSharePointFiles: [],
      sharePointConfigured: false
    });
  };

  const closeCreateProjectModal = () => {
    setCreateProjectModal(false);
    setSharepointCustomFieldId(null);
  };

  // Direct Chat Mode (Project Setup toggle): skip the request-setup form
  // entirely — create a session with a placeholder name and jump straight
  // into chat. The session gets renamed automatically from the user's first
  // question (handled in Analysis.jsx).
  const startDirectChat = async (agent) => {
    const organizationId = userData?.organization_id;
    if (!organizationId) {
      toast.error(t('homepage.noOrganization'));
      return;
    }
    if (isCreatingProject || isStartingDirectChat) return;
    try {
      setIsCreatingProject(true);
      setIsStartingDirectChat(true);
      const taskId = agent.id.toString();

      const historyId = uuidv4();
      const placeholderName = t('homepage.newChatPlaceholderName');
      await createChatHistory(user_id, taskId, historyId, agent.name || '', placeholderName, 'analyse');
      const nowIso = new Date().toISOString();
      dispatch(projectCreated({
        id: historyId, task_id: taskId, user_id, task_name: agent.name || '',
        history_name: placeholderName, timestamp: nowIso, project_image: null, last_activity_at: nowIso,
      }));
      navigate(`/analysis/${historyId}`);
    } catch (error) {
      console.error('Error starting direct chat:', error);
      if (error?.code === 'history_limit_reached') {
        toast.error(t('homepage.historyLimitReached', { limit: error.limit }));
      } else {
        toast.error(error?.message || t('homepage.startDirectChatError'));
      }
    } finally {
      setIsCreatingProject(false);
      setIsStartingDirectChat(false);
    }
  };

  // Handle agent card click — always open Create Project modal with the
  // clicked agent pre-selected (no navigation, regardless of existing projects).
  const handleAgentClick = (agent) => {
    if (agent.direct_chat_mode) {
      startDirectChat(agent);
      return;
    }
    setCreateProjectModal(true);
    setIsAgentPreSelected(true);
    setImportedSharePointFiles([]);
    setSharepointCustomFieldId(null);
    setNewProject({
      agent: agent.id.toString(),
      projectName: '',
      projectPicture: null,
      uploadMethod: '',
      selectedFolders: [],
      uploadedFiles: [],
      selectedSharePointFiles: [],
      sharePointConfigured: false
    });
  };

  const handleProjectPictureUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewProject(prev => ({ ...prev, projectPicture: reader.result, projectPictureFile: file }));
      };
      reader.readAsDataURL(file);
    }
  };

  const deleteProjectPicture = () => {
    setNewProject(prev => ({ ...prev, projectPicture: null, projectPictureFile: null }));
  };

  const handleDocumentUpload = async (e) => {
    const files = Array.from(e.target.files);
    const processedFiles = [];
    // Counts across the WHOLE session's uploadedFiles so far, not just this
    // click — a user picking images across several separate clicks could
    // otherwise exceed the cap by adding a few at a time.
    let acceptedImageCount = newProject.uploadedFiles.filter(f => f.type?.startsWith('image/')).length;

    for (const file of files) {
      let pageCount = 0;

      // Count pages for PDF files
      if (file.type === 'application/pdf') {
        try {
          const arrayBuffer = await file.arrayBuffer();
          const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
          pageCount = pdf.numPages;
          await pdf.destroy();
        } catch (error) {
          console.error(`Error counting pages for ${file.name}:`, error);
          toast.error(`Error processing ${file.name}`);
          continue;
        }
      } else if (file.type?.startsWith('image/')) {
        // Document Intelligence analyzes an image directly — no pages to
        // split/count like a PDF, so this is always exactly 1 for OCR quota
        // purposes (matches ChatInput.jsx/Sidebar.jsx's identical handling).
        if (acceptedImageCount >= 3) {
          toast.error(t('chatActions.tooManyImages', { max: 3 }));
          continue;
        }
        pageCount = 1;
        acceptedImageCount += 1;
      }

      processedFiles.push({
        id: Date.now() + Math.random(),
        name: file.name,
        size: file.size,
        type: file.type,
        file: file,
        pageCount: pageCount
      });
    }

    setNewProject(prev => ({
      ...prev,
      uploadedFiles: [...prev.uploadedFiles, ...processedFiles]
    }));
    // Reset input
    e.target.value = '';
  };

  const removeUploadedFile = (fileId) => {
    setNewProject(prev => ({
      ...prev,
      uploadedFiles: prev.uploadedFiles.filter(f => f.id !== fileId)
    }));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Watch for SharePoint sync results and capture imported files
  useEffect(() => {
    if (syncResults && syncResults.results) {
      const successfulFiles = syncResults.results
        .filter(r => r.status === 'synced')
        .map(r => ({
          id: r.file_id || Date.now() + Math.random(),
          name: r.file_name,
          pageCount: r.page_count || 0,
          status: 'synced'
        }));

      const failedFiles = syncResults.results
        .filter(r => r.status === 'error')
        .map(r => ({
          id: Date.now() + Math.random(),
          name: r.file_name,
          error: r.error,
          status: 'error'
        }));

      if (successfulFiles.length > 0 || failedFiles.length > 0) {
        setImportedSharePointFiles(prev => [...prev, ...successfulFiles, ...failedFiles]);
      }
    }
  }, [syncResults]);

  // Handle SharePoint browser close
  const handleSharePointBrowserClose = () => {
    setShowSharePointBrowser(false);
    dispatch(clearSyncResults());

    if (sharepointCustomFieldId) {
      // Custom file field triggered the browser — route synced files into that field's value.
      const synced = importedSharePointFiles.filter((f) => f.status === 'synced');
      if (synced.length > 0) {
        setCustomFieldValues((prev) => ({
          ...prev,
          [sharepointCustomFieldId]: [...(Array.isArray(prev[sharepointCustomFieldId]) ? prev[sharepointCustomFieldId] : []), ...synced],
        }));
        toast.success('SharePoint-Dateien erfolgreich importiert!');
      } else if (importedSharePointFiles.some((f) => f.status === 'error')) {
        toast.error('Einige Dateien konnten nicht importiert werden.');
      }
      setImportedSharePointFiles([]);
      setSharepointCustomFieldId(null);
    } else {
      // Default (non-custom) flow — keep files in importedSharePointFiles for display.
      setNewProject(prev => ({ ...prev, uploadMethod: 'sharepoint', sharePointConfigured: true }));
      if (importedSharePointFiles.some(f => f.status === 'synced')) {
        toast.success('SharePoint-Dateien erfolgreich importiert!');
      } else if (importedSharePointFiles.some(f => f.status === 'error')) {
        toast.error('Einige Dateien konnten nicht importiert werden. Überprüfen Sie die Liste unten.');
      }
    }
  };

  const handleAnalyze = async () => {
    if (!newProject.agent) {
      toast.error('Bitte wählen Sie einen Agenten aus');
      return;
    }

    // When the agent has Project Setup custom fields, the default Project Name
    // input is hidden; skip its validation. Otherwise it is still required.
    if (customFields.length === 0 && !newProject.projectName) {
      toast.error('Bitte geben Sie einen Projektnamen ein');
      return;
    }

    // Validate required custom fields defined per agent.
    const errs = {};
    customFields.forEach((f) => {
      if (!f.required) return;
      const raw = customFieldValues[f.id];
      if (f.field_type === 'image' || f.field_type === 'file') {
        if (!Array.isArray(raw) || raw.length === 0) errs[f.id] = t('projectSetup.requiredError');
      } else {
        const v = (raw ?? '').toString().trim();
        if (!v) errs[f.id] = t('projectSetup.requiredError');
      }
    });
    if (Object.keys(errs).length > 0) {
      setCustomFieldErrors(errs);
      toast.error(t('projectSetup.requiredError'));
      return;
    }

    const organizationId = userData?.organization_id;
    if (!organizationId) {
      toast.error(t('homepage.noOrganization'));
      return;
    }

    try {
      setIsCreatingProject(true);

      const taskId = newProject.agent;

      // When the agent has custom fields, project documents come from
      // custom-defined file fields (if any) instead of the hidden default
      // upload section. Page-count check is skipped for those (no PDF parsing).
      const useCustomMode = customFields.length > 0;
      const filesToUpload = useCustomMode ? [] : newProject.uploadedFiles.map(f => f.file);
      const totalPages = useCustomMode ? 0 : newProject.uploadedFiles.reduce((sum, f) => sum + (f.pageCount || 0), 0);

      // Page counting and the per-session upload limit are enforced
      // server-side in /api/uploader itself now — see the upload call below.

      // Generate new history ID
      const historyId = uuidv4();

      // Get the selected agent/task details
      const selectedAgent = agentsForModal.find(a => a.id.toString() === taskId.toString());
      const taskName = selectedAgent?.name || '';

      // Resolve project name: in custom-field mode, use the first text/textarea
      // field value as the project title (any label — not just ones containing
      // "name"). This ensures custom text inputs like "Titel", "Bezeichnung",
      // etc. are properly mapped to the DB history_name column. Fall back to
      // the timestamp label only when no text field has a non-empty value.
      let resolvedProjectName = newProject.projectName;
      if (useCustomMode) {
        const nameField = customFields.find((f) =>
          f.field_type === 'text' || f.field_type === 'textarea'
        );
        const fromField = nameField ? (customFieldValues[nameField.id] || '').toString().trim() : '';
        resolvedProjectName = fromField || `${taskName || 'Projekt'} – ${new Date().toLocaleString('de-DE')}`;
      }

      // Create chat history
      const chatHistoryResult = await createChatHistory(
        user_id,
        taskId,
        historyId,
        taskName,
        resolvedProjectName,
        'analyse'
      );

      console.log('Chat history created:', chatHistoryResult);

      // Save custom Project Setup field values for this project (history_id).
      // Image-type fields: upload each File to blob storage first, then store
      // the returned blob_paths (JSON array) as the field value.
      if (customFields.length > 0) {
        try {
          const valuesPayload = [];
          let projectThumbnailSet = false;
          const customFilesToProcess = [];

          for (const f of customFields) {
            const raw = customFieldValues[f.id];
            if (f.field_type === 'image') {
              const fileList = Array.isArray(raw) ? raw : [];
              const blobPaths = [];
              for (const file of fileList) {
                const fd = new FormData();
                fd.append('file', file);
                try {
                  const res = await axios.post(
                    `${BASE_URL}/custom-field-images/${historyId}/${f.id}`,
                    fd,
                    { headers: { 'Content-Type': 'multipart/form-data' } }
                  );
                  if (res.data?.blob_path) blobPaths.push(res.data.blob_path);
                } catch (upErr) {
                  console.error('Custom-field image upload failed:', upErr);
                }
                // Set the first uploaded image as the project thumbnail so it
                // appears in the History list via History.project_image.
                if (!projectThumbnailSet) {
                  try {
                    const thumbFd = new FormData();
                    thumbFd.append('file', file);
                    thumbFd.append('history_id', historyId);
                    await fetch(`${BASE_URL}/projects/upload-image`, { method: 'POST', headers: { ...authHeader() }, body: thumbFd });
                    projectThumbnailSet = true;
                  } catch (thumbErr) {
                    console.error('Project thumbnail upload failed:', thumbErr);
                  }
                }
              }
              valuesPayload.push({ field_id: f.id, value: JSON.stringify(blobPaths) });
            } else if (f.field_type === 'file') {
              const fileList = Array.isArray(raw) ? raw : [];
              const refs = [];
              for (const file of fileList) {
                if (file instanceof File) {
                  // Collect for the document pipeline (embedding / AI indexing).
                  customFilesToProcess.push(file);
                  const fd = new FormData();
                  fd.append('file', file);
                  try {
                    const res = await axios.post(
                      `${BASE_URL}/custom-field-files/${historyId}/${f.id}`,
                      fd,
                      { headers: { 'Content-Type': 'multipart/form-data' } }
                    );
                    if (res.data?.blob_path) {
                      refs.push({
                        blob_path: res.data.blob_path,
                        filename: res.data.filename || file.name,
                      });
                    }
                  } catch (upErr) {
                    console.error('Custom-field file upload failed:', upErr);
                  }
                } else if (file.status === 'synced') {
                  refs.push({ sp_file_id: file.id, filename: file.name, source: 'sharepoint' });
                }
              }
              valuesPayload.push({ field_id: f.id, value: JSON.stringify(refs) });
            } else {
              valuesPayload.push({ field_id: f.id, value: (raw ?? '').toString() });
            }
          }
          await axios.put(`${BASE_URL}/project-custom-field-values/${historyId}`, { values: valuesPayload }, authHeaders());

          // Send custom file field documents through the regular upload pipeline
          // so they are embedded and available for AI analysis.
          if (customFilesToProcess.length > 0) {
            try {
              await uploadFilesWithHistory(
                customFilesToProcess,
                user_id,
                taskId,
                historyId,
                customFilesToProcess.map(() => 0),
              );
            } catch (fileErr) {
              console.error('Custom file processing failed:', fileErr);
              // Everything else stays silent as before, but a page-limit
              // rejection must be visible — otherwise the files simply never
              // arrive with no explanation.
              if (fileErr?.code === 'upload_page_limit_exceeded') {
                toast.error(t('chatActions.uploadPageLimitExceeded', { limit: fileErr.limit, attempted: fileErr.attempted }));
              }
            }
          }
        } catch (cfErr) {
          console.error('Custom field values save failed:', cfErr);
        }
      }

      // Upload project image if selected (skipped in custom-fields mode where
      // the default Project Picture input is hidden).
      if (!useCustomMode && newProject.projectPicture && newProject.projectPictureFile) {
        try {
          const imgFormData = new FormData()
          imgFormData.append('file', newProject.projectPictureFile)
          imgFormData.append('history_id', historyId)
          await fetch(`${BASE_URL}/projects/upload-image`, { method: 'POST', headers: { ...authHeader() }, body: imgFormData })
        } catch (imgErr) {
          console.error('Project image upload failed:', imgErr)
        }
      }

      // Upload files if any
      if (filesToUpload.length > 0) {
        const pageCounts = newProject.uploadedFiles.map(f => f.pageCount || 0);
        const uploadResult = await uploadFilesWithHistory(
          filesToUpload,
          user_id,
          taskId,
          historyId,
          pageCounts
        );
        console.log('Files uploaded:', uploadResult);
      }

      // Close modal and navigate to analysis page
      const nowIso = new Date().toISOString();
      dispatch(projectCreated({
        id: historyId, task_id: taskId, user_id, task_name: taskName,
        history_name: resolvedProjectName, timestamp: nowIso, project_image: null, last_activity_at: nowIso,
      }));
      closeCreateProjectModal();
      toast.success('Projekt erfolgreich erstellt!');
      navigate(`/analysis/${historyId}`);

    } catch (error) {
      console.error('Error creating project:', error);
      let errorMessage = t('homepage.createProjectError');
      if (error?.code === 'history_limit_reached') {
        errorMessage = t('homepage.historyLimitReached', { limit: error.limit });
      } else if (error?.code === 'upload_page_limit_exceeded') {
        errorMessage = t('chatActions.uploadPageLimitExceeded', { limit: error.limit, attempted: error.attempted });
      } else if (error?.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
    } finally {
      setIsCreatingProject(false);
    }
  };

  // Handle edit start
  const handleEditStart = (project) => {
    setEditingProject(project.id);
    setEditTitle(project.title);
    setOpenDropdown(null);
  };

  // Handle edit save
  const handleEditSave = async (projectId) => {
    try {
      await updateHistoryName(projectId, editTitle);
      dispatch(projectRenamed({ id: projectId, history_name: editTitle }));
      setEditingProject(null);
      setEditTitle('');
    } catch (error) {
      console.error('Failed to update project name:', error);
    }
  };

  // Handle view detail (continue to analysis)
  const handleViewDetail = (projectId) => {
    navigate(`/analysis/${projectId}`);
    setOpenDropdown(null);
  };

  // Handle view project detail page — respects per-agent overviewEnabled toggle
  // (the dedicated "AI Request Overview" switch, not the unrelated Standard
  // Questions toggle - same as History.jsx).
  const handleViewProjectDetail = (projectId, projectName, taskId = null) => {
    const taskIdStr = taskId ? taskId.toString() : null;
    const showDetail = taskIdStr !== null
      ? (overviewEnabledByTask[taskIdStr] !== false)
      : true;
    if (showDetail) {
      navigate(`/project-detail/${projectId}`, { state: { projectName } });
    } else {
      navigate(`/analysis/${projectId}`);
    }
    setOpenDropdown(null);
  };

  // Fetch data on mount — skip the history fetch if already loaded (create/
  // rename/delete keep it in sync locally now).
  useEffect(() => {
    if (user_id && status !== 'succeeded' && status !== 'loading') {
      dispatch(fetchChatHistoryByUser(user_id));
    }
    if (email) {
      dispatch(fetchTasksAsync(email));
    }
  }, [dispatch, user_id, email, status]);

  // Fetch departments for the org so we can apply admin-set sort_order on home tabs.
  useEffect(() => {
    const orgId = userData?.organization_id;
    if (orgId && reduxDepartments.length === 0) {
      dispatch(fetchDepartments(orgId));
    }
  }, [dispatch, userData?.organization_id]);

  // Helper function to check if task_id is a valid numeric ID (not a UUID)
  const isValidNumericTaskId = (taskId) => {
    if (!taskId) return false;
    const taskIdStr = taskId.toString();
    // UUIDs contain hyphens, numeric IDs don't
    // Also check if it's actually a number
    return !taskIdStr.includes('-') && !isNaN(Number(taskIdStr));
  };

  // Fetch agent layouts for showProjectDetailByTask flags
  useEffect(() => {
    if (chatHistory?.length > 0) {
      const fetched = new Set();
      chatHistory.forEach(record => {
        if (!record.task_id) return;
        const tid = record.task_id.toString();
        if (fetched.has(tid) || layoutLoadingByTask[tid] || layoutByTask[tid]) return;
        fetched.add(tid);
        dispatch(fetchAgentLayout(tid));
      });
    }
  }, [chatHistory, dispatch, layoutLoadingByTask, layoutByTask]);

  // Fetch progress data for each project
  useEffect(() => {
    if (chatHistory?.length > 0) {
      setHistoryData(new Map());
      chatHistory.forEach(record => {
        if (record.task_id && record.id) {
          // Only fetch SQ data if task_id is a valid numeric ID (not a UUID)
          if (isValidNumericTaskId(record.task_id)) {
            dispatch(fetchSQByTaskId(record.task_id))
              .then((action) => {
                if (action.payload) {
                  setHistoryData(prev => {
                    const newMap = new Map(prev);
                    const currentData = newMap.get(record.id) || {};
                    newMap.set(record.id, {
                      ...currentData,
                      questions: action.payload.questions || []
                    });
                    return newMap;
                  });
                }
              });
          }

          dispatch(getTaskStatusesByHistoryAsync(record.id))
            .then((action) => {
              if (action.payload) {
                setHistoryData(prev => {
                  const newMap = new Map(prev);
                  const currentData = newMap.get(record.id) || {};
                  newMap.set(record.id, {
                    ...currentData,
                    statuses: action.payload || []
                  });
                  return newMap;
                });
              }
            });
        }
      });
    }
  }, [chatHistory, dispatch]);

  // Logged-in user's assigned user-groups (departments) — set by an admin via
  // the Administrator → "Organisation zuweisen" modal. When non-empty, only
  // agents in those user-groups are visible on the homepage.
  const userDepartmentNames = Array.isArray(userData?.department_names) ? userData.department_names : [];

  // Each agent's user-group (department) was selected when the agent was created.
  // Agents without an assigned user-group are excluded — only real user-groups
  // become tabs.
  // state.tasks.tasks is shared with Agents.jsx/ManageTasks.jsx, which
  // sometimes populate it with an unscoped, all-organizations task list —
  // re-scoping here keeps the homepage showing only the current user's own
  // org (+ global tasks) no matter what those pages left behind.
  const allAgents = tasks.length > 0
    ? tasks
        .filter((task) => !!task.department_name && task.name !== '__CHAT_INTERNAL__'
          && (task.organization_id === userData?.organization_id || task.organization_id == null))
        .map((task, index) => ({
          id: task.id,
          name: task.name || 'Agent',
          description: task.description || t('homepage.agentCardDefaultDescription'),
          department: task.department_name,
          display_position: task.display_position,
          CreatedAt: task.CreatedAt,
          // Forward the image marker from the API so AgentCard can render the
          // admin-uploaded picture (served by /agents/media/:id) instead of
          // always falling back to the default AgentIcon.
          image_blob_path: task.image_blob_path,
          highlighted: index % 2 === 1,
          direct_chat_mode: !!task.direct_chat_mode,
        }))
    : [];

  const isAdminRole  = userData?.user_role === 'ADMIN' || userData?.user_role === 'SUPER_ADMIN';
  const isNormalUser = userData?.user_role === 'normal';

  // ITEM 1: Admin/Super-Admin are part of ALL departments automatically —
  // their department assignments are ignored for agent visibility.
  // Normal users see only agents from their assigned departments.
  const agents = !userData
    ? []
    : isAdminRole
      ? allAgents
      : isNormalUser
        ? (userDepartmentNames.length > 0
            ? allAgents.filter((a) => userDepartmentNames.includes(a.department))
            : [])
        : [];

  // ADMIN 1: Create-project modal — admin/super-admin see ALL agents (no department filter).
  // Normal users keep the same department-scoped list.
  const agentsForModal = !userData
    ? []
    : isAdminRole
      ? tasks
          .filter((task) => task.name !== '__CHAT_INTERNAL__'
            && (task.organization_id === userData?.organization_id || task.organization_id == null))
          .map((task, index) => ({
            id: task.id,
            name: task.name || 'Agent',
            description: task.description || t('homepage.agentCardDefaultDescription'),
            department: task.department_name,
            image_blob_path: task.image_blob_path,
            highlighted: index % 2 === 1,
            direct_chat_mode: !!task.direct_chat_mode,
          }))
      : agents;

  // Whether any real (non-internal) agent exists in the system. Gates the
  // Create Project button independent of department filtering.
  const hasUsableAgents = tasks.some((task) => task.name !== '__CHAT_INTERNAL__');

  // Unique department tabs derived from agents, sorted by admin-set sort_order from DB.
  const departments = (() => {
    const raw = [...new Set(agents.map(agent => agent.department))];
    if (reduxDepartments.length === 0) return raw;
    const orderMap = {};
    reduxDepartments.forEach(d => { orderMap[d.department_name] = d.sort_order ?? 0; });
    return [...raw].sort((a, b) => {
      const ao = orderMap[a] ?? 9999;
      const bo = orderMap[b] ?? 9999;
      return ao - bo;
    });
  })();

  // Set initial active department to first available if current is not in list.
  useEffect(() => {
    if (departments.length > 0 && !departments.includes(activeDepartment)) {
      setActiveDepartment(departments[0]);
    }
  }, [departments, activeDepartment]);

  const filteredAgents = agents.filter(agent => agent.department === activeDepartment).sort(compareAgentOrder);
  const AGENTS_PER_DEPARTMENT = 4;
  const visibleAgents = showAllAgents ? filteredAgents : filteredAgents.slice(0, AGENTS_PER_DEPARTMENT);
  const hasMoreAgents = filteredAgents.length > AGENTS_PER_DEPARTMENT;

  // Reset the "Show All" toggle when switching departments — user expects the
  // 4-card preview again, not the previous tab's expanded state.
  useEffect(() => {
    setShowAllAgents(false);
  }, [activeDepartment]);

  const formatTimestamp = (timestamp) => {
    try {
      const date = parseISO(timestamp);
      return format(date, 'MMM d, yyyy');
    } catch {
      return 'Invalid Date';
    }
  };

  const isQuestionsEnabled = (taskId) => {
    if (!taskId) return true;
    return showProjectDetailByTask[taskId.toString()] !== false;
  };

  const getCompletedQuestions = (historyId, taskId = null) => {
    if (!isQuestionsEnabled(taskId)) return { completed: 0, total: 0, disabled: true };
    const data = historyData.get(historyId) || {};
    const questions = data.questions || [];
    const statuses = data.statuses || [];
    if (!questions.length) return { completed: 0, total: 0 };
    const validSqIds = new Set(questions.map(q => parseInt(q.id)));
    const completedCount = statuses.filter(
      ts => ts.status === true && validSqIds.has(parseInt(ts.sq_id))
    ).length;
    return { completed: completedCount, total: questions.length };
  };

  const getProjectStatuses = (historyId, taskId = null) => {
    if (!isQuestionsEnabled(taskId)) return null; // null = disabled
    const data = historyData.get(historyId) || {};
    const questions = data.questions || [];
    const statuses = data.statuses || [];
    if (!questions.length) return [];
    const validSqIds = new Set(questions.map(q => parseInt(q.id)));
    return statuses.filter(ts => validSqIds.has(parseInt(ts.sq_id)));
  };

  // Get all projects sorted by most recent activity (newest first) — uses
  // last_activity_at (bumped on every chat message) so a chat someone is
  // actively using surfaces here even if it was created a while ago, not
  // just whichever chat was CREATED most recently. Falls back to timestamp
  // (creation time) for older rows from before this column existed. The
  // displayed `date` below is still the creation timestamp, unchanged.
  const recentProjects = Array.isArray(chatHistory)
    ? [...chatHistory]
        // Same department scoping as filteredAgents above — a history
        // entry's task_id is joined against `agents` to find its
        // department, then filtered by the active tab the same way.
        .filter(item => {
          if (!activeDepartment) return true;
          const agent = agents.find(a => a.id?.toString() === item.task_id?.toString());
          return agent?.department === activeDepartment;
        })
        .sort((a, b) => {
          const dateA = new Date(a.last_activity_at || a.timestamp || 0);
          const dateB = new Date(b.last_activity_at || b.timestamp || 0);
          return dateB - dateA;
        })
        .map(item => ({
          id: item.id,
          title: item.history_name || item.task_name || 'Unbenanntes Projekt',
          agent: item.task_name || 'Agent',
          date: item.timestamp,
          taskId: item.task_id,
          projectImage: item.project_image || null,
        }))
    : [];

  // Orange/Coral button color
  const buttonColor = aispaceColor;

  return (
    <div className="h-screen bg-white overflow-hidden flex flex-col">
      <Toaster position="top-right" />
      {isStartingDirectChat && (
        <div className="fixed inset-0 bg-white/70 backdrop-blur-[1px] z-[400] flex flex-col items-center justify-center gap-3">
          <div
            className="w-10 h-10 border-[3px] border-t-transparent rounded-full animate-spin"
            style={{ borderColor: aispaceColor, borderTopColor: 'transparent' }}
          />
          <p className="text-[14px] font-medium text-[#374151]">{t('homepage.startingDirectChat')}</p>
        </div>
      )}
      {/* Header */}
      <header className="px-10 h-[80px] flex items-center flex-shrink-0">
        <h1 className="text-[22px] font-bold text-[#111827]">{t('nav.homepage')}</h1>
      </header>

      <main className="px-10 pb-10 flex-1 overflow-y-auto thin-scrollbar">
        {/* Agents Section */}
        <section className="mb-10">
          {tasksStatus === 'loading' && tasks.length === 0 ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: aispaceColor, borderTopColor: 'transparent' }}></div>
            </div>
          ) : departments.length > 0 ? (
            <>
              {/* Benutzergruppen-Tabs (User Group tabs) */}
              <div className="flex items-center gap-7 mb-6 border-b border-[#E5E7EB] overflow-x-auto thin-scrollbar">
                {departments.map((department) => (
                  <button
                    key={department}
                    onClick={() => setActiveDepartment(department)}
                    className={`pb-3 text-[14px] font-medium transition-colors relative whitespace-nowrap ${
                      activeDepartment === department
                        ? ''
                        : 'text-[#9CA3AF] hover:text-[#6B7280]'
                    }`}
                    style={{
                      color: activeDepartment === department ? aispaceColor : undefined
                    }}
                  >
                    {department}
                    {activeDepartment === department && (
                      <div
                        className="absolute bottom-0 left-0 right-0 h-[2px]"
                        style={{ backgroundColor: aispaceColor }}
                      />
                    )}
                  </button>
                ))}
              </div>

              {/* Agent Cards Grid — first 4 by default, expand on demand */}
              <div className={showAllAgents ? "max-h-[480px] overflow-y-auto thin-scrollbar pr-2" : ""}>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {visibleAgents.map((agent) => (
                    <AgentCard
                      key={agent.id}
                      agent={agent}
                      themeColor={aispaceColor}
                      onClick={() => handleAgentClick(agent)}
                    />
                  ))}
                </div>
              </div>

              {hasMoreAgents && (
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={() => setShowAllAgents(prev => !prev)}
                    className="px-4 py-1.5 text-[13px] font-medium text-[#374151] border border-[#E5E7EB] rounded-md bg-white hover:bg-[#F9FAFB] transition-colors"
                  >
                    {showAllAgents ? t('homepage.showLess') : t('homepage.showAll')}
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-5">
              {/* Decorative circle with + icon */}
              <div className="relative mb-5">
                <div className="w-[100px] h-[100px] rounded-full border-2 border-dashed border-[#E5E7EB] flex items-center justify-center relative">
                  {/* Decorative dots */}
                  <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-[#E5E7EB]"></div>
                  <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-[#E5E7EB]"></div>
                  <div className="absolute top-1/2 -left-1 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-[#E5E7EB]"></div>
                  <div className="absolute top-1/2 -right-1 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-[#E5E7EB]"></div>
                  {/* Inner circle with + icon */}
                  <div className="w-12 h-12 rounded-full bg-[#F9FAFB] border border-[#E5E7EB] flex items-center justify-center">
                    <Plus size={22} className="text-[#9CA3AF]" />
                  </div>
                </div>
              </div>
              <h3 className="text-[15px] font-semibold text-[#111827] mb-1.5">{t('homepage.noAgentsYet')}</h3>
              <p className="text-[13px] text-[#6B7280]">{t('homepage.noAgentsYetHint')}</p>
            </div>
          )}
        </section>

        {/* Recent Projects Section */}
        <section>
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-[18px] font-semibold text-[#111827]">{t('homepage.recentProjects')}</h2>
            <button
              onClick={openCreateProjectModal}
              disabled={!hasUsableAgents}
              title={!hasUsableAgents ? t('homepage.createAgentFirst') : undefined}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-white text-[14px] font-medium transition-colors hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:opacity-50"
              style={{ backgroundColor: buttonColor }}
            >
              <Plus size={18} strokeWidth={2.5} />
              {t('homepage.createNewProject')}
            </button>
          </div>

          {/* Projects Table */}
          <div className={`bg-white ${status !== 'loading' && recentProjects.length === 0 ? '' : 'rounded-lg border border-[#E5E7EB] overflow-x-auto'}`}>
            {/* Table Header — hidden in empty state */}
            {!(status !== 'loading' && recentProjects.length === 0) && (
              <div className="flex items-center px-6 py-3 border-b border-[#E5E7EB] min-w-[980px]">
                <div className="flex-1 min-w-[180px] text-[13px] font-medium text-[#6B7280]">{t('homepage.tableTitle')}</div>
                <div className="w-[100px] flex-shrink-0"></div>
                <div className="w-[160px] flex-shrink-0 text-[13px] font-medium text-[#6B7280]">{t('homepage.tableAgents')}</div>
                <div className="w-[120px] flex-shrink-0 text-[13px] font-medium text-[#6B7280]">{t('homepage.tableCreatedAt')}</div>
                <div className="w-[120px] flex-shrink-0 text-[13px] font-medium text-[#6B7280]">{t('homepage.tableProgress')}</div>
                <div className="w-[180px] flex-shrink-0 text-[13px] font-medium text-[#6B7280]">{t('homepage.tableAnswerStatus')}</div>
                <div className="w-[140px] flex-shrink-0 ml-4"></div>
              </div>
            )}

            {/* Table Body with scroll */}
            {status === 'loading' ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: aispaceColor, borderTopColor: 'transparent' }}></div>
              </div>
            ) : recentProjects.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                {/* Decorative circle with + icon */}
                <div className="relative mb-5">
                  <div className="w-[100px] h-[100px] rounded-full border-2 border-dashed border-[#E5E7EB] flex items-center justify-center relative">
                    {/* Decorative dots */}
                    <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-[#E5E7EB]"></div>
                    <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-[#E5E7EB]"></div>
                    <div className="absolute top-1/2 -left-1 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-[#E5E7EB]"></div>
                    <div className="absolute top-1/2 -right-1 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-[#E5E7EB]"></div>
                    {/* Inner circle with + icon */}
                    <div className="w-12 h-12 rounded-full bg-[#F9FAFB] border border-[#E5E7EB] flex items-center justify-center">
                      <Plus size={22} className="text-[#9CA3AF]" />
                    </div>
                  </div>
                </div>
                <h3 className="text-[15px] font-semibold text-[#111827] mb-1.5">{t('homepage.noProjectsCreated')}</h3>
                <p className="text-[13px] text-[#6B7280]">{t('homepage.noProjectsHint')}</p>
              </div>
            ) : (
              <div className="max-h-[350px] overflow-y-auto thin-scrollbar">
                {recentProjects.map((project, index) => {
                  const { completed, total, disabled: qaDisabled } = getCompletedQuestions(project.id, project.taskId);
                  // Show dropdown above if there are 1-2 items OR current is in last 2
                  const shouldDropdownOpenUp = recentProjects.length <= 2 || index >= recentProjects.length - 2;
                  const isEditing = editingProject === project.id;

                  return (
                    <div
                      key={project.id}
                      className={`flex items-center px-6 py-4 hover:bg-[#FAFAFA] transition-colors min-w-[980px] cursor-pointer ${
                        index !== recentProjects.length - 1 ? 'border-b border-[#F3F4F6]' : ''
                      }`}
                      onClick={() => {
                        if (isEditing) return;
                        handleViewProjectDetail(project.id, project.title, project.taskId);
                      }}
                    >
                      {/* Title */}
                      <div className="flex-1 min-w-[180px] flex items-center gap-3">
                        <ProjectImage projectId={project.id} imagePath={project.projectImage} type="icon" />
                        {isEditing ? (
                          <div className="flex items-center gap-2 flex-1">
                            <input
                              type="text"
                              value={editTitle}
                              onChange={(e) => setEditTitle(e.target.value)}
                              className="flex-1 px-2 py-1 text-[14px] border border-gray-300 rounded focus:outline-none focus:border-[#8B5CF6]"
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleEditSave(project.id);
                                if (e.key === 'Escape') {
                                  setEditingProject(null);
                                  setEditTitle('');
                                }
                              }}
                            />
                            <button
                              onClick={(e) => { e.stopPropagation(); handleEditSave(project.id); }}
                              className="p-1 text-green-600 hover:text-green-700"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M20 6L9 17l-5-5"/>
                              </svg>
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingProject(null);
                                setEditTitle('');
                              }}
                              className="p-1 text-gray-400 hover:text-gray-600"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M18 6L6 18M6 6l12 12"/>
                              </svg>
                            </button>
                          </div>
                        ) : (
                          <span className="text-[14px] font-medium text-[#111827] truncate">{project.title}</span>
                        )}
                      </div>

                      {/* Shared badge column */}
                      <div className="w-[100px] flex-shrink-0">
                        {sharedHistoryIds.has(String(project.id)) && (
                          <span className="text-[11px] font-semibold px-2 py-0.5 rounded-md"
                            style={{ backgroundColor: `${aispaceColor}18`, color: aispaceColor }}>
                            {t('history.accessViewOnly')}
                          </span>
                        )}
                      </div>

                      {/* Agent */}
                      <div className="w-[160px] flex-shrink-0 text-[14px] text-[#374151] truncate">
                        {project.agent}
                      </div>

                      {/* Date */}
                      <div className="w-[120px] flex-shrink-0 text-[14px] text-[#6B7280]">
                        {formatTimestamp(project.date)}
                      </div>

                      {/* Progress */}
                      <div className="w-[120px] flex-shrink-0 text-[14px] text-[#6B7280]">
                        {qaDisabled
                          ? <span className="block text-center">—</span>
                          : t('homepage.answeredOf', { done: completed, total })}
                      </div>

                      {/* Antwort-Status */}
                      <div className="w-[180px] flex-shrink-0">
                        {(() => {
                          const statuses = getProjectStatuses(project.id, project.taskId);
                          if (statuses === null) return <span className="block text-center text-[14px] text-[#6B7280]">—</span>;
                          return <AnswerStatusBadges statuses={statuses} size="xs" />;
                        })()}
                      </div>

                      {/* Actions */}
                      <div className="w-[140px] flex-shrink-0 flex items-center justify-end gap-2 relative ml-4">
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate(`/analysis/${project.id}`); }}
                          className="px-4 py-1.5 text-[13px] font-medium text-white rounded-md transition-colors hover:opacity-90"
                          style={{ backgroundColor: buttonColor }}
                        >
                          {t('history.continue')}
                        </button>
                        <div className="relative">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (openDropdown === project.id) {
                                setOpenDropdown(null);
                              } else {
                                const rect = e.currentTarget.getBoundingClientRect();
                                const dropdownHeight = 180; // Approximate dropdown height
                                const spaceBelow = window.innerHeight - rect.bottom;
                                const openUp = shouldDropdownOpenUp || spaceBelow < dropdownHeight;

                                setDropdownPosition({
                                  top: openUp ? rect.top - dropdownHeight - 8 : rect.bottom + 8,
                                  left: rect.right - 176, // 176px = w-44 (11rem)
                                  openUp
                                });
                                setOpenDropdown(project.id);
                              }
                            }}
                            className="p-1.5 text-[#9CA3AF] hover:text-[#6B7280] hover:bg-gray-100 border rounded-lg transition-colors"
                          >
                            <MoreVertical size={18} />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      </main>

      {/* Fixed Dropdown Menu - rendered outside overflow container */}
      {openDropdown && (
        <div
          ref={dropdownRef}
          className="fixed w-56 bg-white border border-[#E5E7EB] rounded-lg shadow-xl z-[9999] py-1"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`
          }}
        >
          <button
            onClick={() => {
              const project = recentProjects.find(p => p.id === openDropdown);
              if (project) handleViewProjectDetail(project.id, project.title, project.taskId);
            }}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-[14px] text-[#374151] hover:bg-[#F9FAFB] transition-colors"
          >
            <Eye size={16} className="text-[#6B7280]" />
            <span>{t('history.projectDetails')}</span>
          </button>
          <button
            onClick={() => {
              const project = recentProjects.find(p => p.id === openDropdown);
              if (project) { setOpenDropdown(null); setShareTarget({ id: project.id, name: project.title }); }
            }}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-[14px] text-[#374151] hover:bg-[#F9FAFB] transition-colors"
          >
            <Share2 size={16} className="text-[#6B7280]" />
            <span>{t('share.shareProject')}</span>
          </button>
          <button
            onClick={() => {
              const project = recentProjects.find(p => p.id === openDropdown);
              if (project) handleEditStart(project);
            }}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-[14px] text-[#374151] hover:bg-[#F9FAFB] transition-colors"
          >
            <Pencil size={16} className="text-[#6B7280]" />
            <span>{t('common.edit')}</span>
          </button>
          <button
            onClick={() => {
              const project = recentProjects.find(p => p.id === openDropdown);
              if (project) handleDeleteClick(project);
            }}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-[14px] text-[#EF4444] hover:bg-[#FEF2F2] transition-colors"
          >
            <Trash2 size={16} />
            <span>{t('common.delete')}</span>
          </button>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal.isOpen && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={cancelDelete}
          />

          {/* Modal */}
          <div className="relative bg-white rounded-xl shadow-xl w-[400px] p-6 mx-4">
            {/* Warning Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-12 h-12 rounded-full bg-[#FEE2E2] flex items-center justify-center">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="#EF4444" strokeWidth="2"/>
                  <path d="M12 8V12" stroke="#EF4444" strokeWidth="2" strokeLinecap="round"/>
                  <circle cx="12" cy="16" r="1" fill="#EF4444"/>
                </svg>
              </div>
            </div>

            {/* Title */}
            <h3 className="text-[18px] font-semibold text-[#111827] text-center mb-2">
              Projekt löschen
            </h3>

            {/* Description */}
            <p className="text-[14px] text-[#6B7280] text-center mb-6">
              Sind Sie sicher, dass Sie dieses Projekt löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.
            </p>

            {/* Buttons */}
            <div className="flex gap-3">
              <button
                onClick={cancelDelete}
                className="flex-1 px-4 py-2.5 text-[14px] font-medium text-[#374151] bg-white border border-[#D1D5DB] rounded-lg hover:bg-[#F9FAFB] transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={confirmDelete}
                disabled={deletingId}
                className="flex-1 px-4 py-2.5 text-[14px] font-medium text-white bg-[#EF4444] rounded-lg hover:bg-[#DC2626] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {deletingId ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {t('deleteModal.deleting')}
                  </>
                ) : (
                  t('common.delete')
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Project Modal */}
      {createProjectModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={isCreatingProject ? undefined : closeCreateProjectModal}
          />

          {/* Modal */}
          <div className="relative bg-white rounded-xl shadow-xl w-[520px] max-h-[90vh] mx-4 flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-[#E5E7EB]">
              <h2 className="text-[20px] font-semibold text-[#111827]">{t('createProjectModal.title')}</h2>
              <button
                onClick={closeCreateProjectModal}
                disabled={isCreatingProject}
                className="p-1 hover:bg-[#F3F4F6] rounded-lg transition-colors disabled:opacity-50"
              >
                <X size={20} className="text-[#6B7280]" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
              {/* Agent Dropdown - custom */}
              <div>
                <label className="block text-[14px] font-semibold text-[#111827] mb-2">{t('createProjectModal.agentLabel')}</label>
                <div className="relative" ref={agentDropdownRef}>
                  {(() => {
                    const selectedAgent = agentsForModal.find(a => a.id.toString() === newProject.agent)
                    const label = selectedAgent ? selectedAgent.name : t('createProjectModal.agentPlaceholder')
                    return (
                      <>
                        <button
                          type="button"
                          ref={agentTriggerRef}
                          onClick={() => {
                            if (isAgentPreSelected) return;
                            if (!agentDropdownOpen && agentTriggerRef.current) {
                              const r = agentTriggerRef.current.getBoundingClientRect();
                              setAgentMenuPos({ top: r.bottom + 4, left: r.left, width: r.width });
                            }
                            setAgentDropdownOpen(v => !v);
                          }}
                          disabled={isAgentPreSelected}
                          className={`w-full flex items-center justify-between px-4 py-3 text-[14px] border border-[#E5E7EB] rounded-lg focus:outline-none transition-colors ${
                            isAgentPreSelected ? 'bg-[#F3F4F6] cursor-not-allowed text-[#6B7280]' : 'bg-white hover:bg-[#F9FAFB] cursor-pointer'
                          }`}
                          style={agentDropdownOpen ? { borderColor: aispaceColor } : {}}
                        >
                          <span className={selectedAgent ? 'text-[#111827]' : 'text-[#9CA3AF]'}>{label}</span>
                          <ChevronDown size={16} className={`transition-transform ${agentDropdownOpen ? 'rotate-180' : ''} ${isAgentPreSelected ? 'text-[#9CA3AF]' : 'text-[#6B7280]'}`} />
                        </button>
                        {agentDropdownOpen && (
                          <div
                            className="bg-white border border-[#E5E7EB] rounded-lg shadow-lg z-[300] max-h-[260px] overflow-y-auto py-1"
                            style={{ position: 'fixed', top: agentMenuPos?.top, left: agentMenuPos?.left, width: agentMenuPos?.width }}
                          >
                            {agentsForModal.length === 0 ? (
                              <div className="px-4 py-3 text-[13px] text-[#9CA3AF]">{t('createProjectModal.noAgentsAvailable')}</div>
                            ) : (
                              agentsForModal.map(agent => {
                                const isSelected = agent.id.toString() === newProject.agent
                                return (
                                  <button
                                    key={agent.id}
                                    type="button"
                                    onClick={() => {
                                      setAgentDropdownOpen(false)
                                      if (agent.direct_chat_mode) {
                                        closeCreateProjectModal()
                                        startDirectChat(agent)
                                        return
                                      }
                                      setNewProject(prev => ({ ...prev, agent: agent.id.toString() }))
                                    }}
                                    className={`w-full px-4 py-2.5 flex items-center justify-between text-left text-[14px] text-[#111827] hover:bg-[#F9FAFB] transition-colors ${isSelected ? 'bg-[#F3F4F6]' : ''}`}
                                  >
                                    <span className="truncate">{agent.name}</span>
                                    {isSelected && <Check size={16} style={{ color: aispaceColor }} />}
                                  </button>
                                )
                              })
                            )}
                          </div>
                        )}
                      </>
                    )
                  })()}
                </div>
              </div>

              {/* All other fields are revealed only after an agent is chosen. */}
              {newProject.agent && (
              <>
              {/* While the agent's custom-field schema is loading, show a
                  spinner instead of momentarily flashing the default fields. */}
              {customFieldsLoading && (
                <div className="flex items-center justify-center py-10">
                  <div
                    className="w-7 h-7 border-2 border-t-transparent rounded-full animate-spin"
                    style={{ borderColor: aispaceColor, borderTopColor: 'transparent' }}
                  />
                </div>
              )}
              {/* Default fields (Project Picture + Name) — only shown once
                  loading is done AND the agent has no custom fields. */}
              {!customFieldsLoading && customFields.length === 0 && (
              <>
              <div>
                <label className="block text-[14px] font-semibold text-[#111827] mb-3">{t('createProjectModal.projectImageLabel')}</label>
                <div className="flex items-center gap-4">
                  {/* Preview */}
                  <div className="w-[72px] h-[72px] rounded-full bg-gradient-to-br from-[#E0F2FE] via-[#DBEAFE] to-[#EDE9FE] flex items-center justify-center overflow-hidden">
                    {newProject.projectPicture ? (
                      <img src={newProject.projectPicture} alt="Project" className="w-full h-full object-cover" />
                    ) : (
                      <svg className="w-10 h-10 opacity-80" viewBox="0 0 40 40" fill="none">
                        <path d="M20 10L10 18L20 26L30 18L20 10Z" fill="url(#previewGrad1)" />
                        <path d="M10 24L20 32L30 24" stroke="url(#previewGrad2)" strokeWidth="2" strokeLinecap="round"/>
                        <defs>
                          <linearGradient id="previewGrad1" x1="10" y1="10" x2="30" y2="26" gradientUnits="userSpaceOnUse">
                            <stop stopColor="#38BDF8"/><stop offset="1" stopColor="#818CF8"/>
                          </linearGradient>
                          <linearGradient id="previewGrad2" x1="10" y1="24" x2="30" y2="32" gradientUnits="userSpaceOnUse">
                            <stop stopColor="#60A5FA"/><stop offset="1" stopColor="#A78BFA"/>
                          </linearGradient>
                        </defs>
                      </svg>
                    )}
                  </div>

                  {/* Upload Button */}
                  <input
                    type="file"
                    ref={projectPictureRef}
                    onChange={handleProjectPictureUpload}
                    accept="image/*"
                    className="hidden"
                  />
                  <button
                    onClick={() => projectPictureRef.current?.click()}
                    className="px-5 py-2.5 text-[14px] font-medium text-white rounded-lg transition-colors hover:opacity-90"
                    style={{ backgroundColor: aispaceColor }}
                  >
                    {newProject.projectPicture ? t('createProjectModal.uploadNewImage') : t('createProjectModal.uploadImage')}
                  </button>

                  {newProject.projectPicture ? (
                    <button
                      onClick={deleteProjectPicture}
                      className="px-4 py-2.5 text-[14px] font-medium text-[#EF4444] hover:text-[#DC2626] transition-colors"
                    >
                      {t('common.delete')}
                    </button>
                  ) : (
                    <span className="text-[14px] text-[#9CA3AF]">{t('createProjectModal.noFileSelected')}</span>
                  )}
                </div>
              </div>

              {/* Project Name */}
              <div>
                <label className="block text-[14px] font-semibold text-[#111827] mb-2">{t('createProjectModal.projectNameLabel')}</label>
                <input
                  type="text"
                  value={newProject.projectName}
                  onChange={(e) => setNewProject(prev => ({ ...prev, projectName: e.target.value }))}
                  placeholder={t('createProjectModal.projectNamePlaceholder')}
                  className="w-full px-4 py-3 text-[14px] border border-[#E5E7EB] rounded-lg focus:outline-none"
                  onFocus={(e) => e.target.style.borderColor = aispaceColor}
                  onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                />
              </div>
              </>
              )}

              {/* Custom fields defined per agent (Agent Settings → Project Setup) */}
              {customFields.map((field) => {
                const error = customFieldErrors[field.id];
                if (field.field_type === 'image') {
                  const fileList = Array.isArray(customFieldValues[field.id]) ? customFieldValues[field.id] : [];
                  return (
                    <CustomImageField
                      key={field.id}
                      field={field}
                      value={fileList}
                      onChange={(files) => {
                        setCustomFieldValues((prev) => ({ ...prev, [field.id]: files }));
                        if (error) setCustomFieldErrors((prev) => ({ ...prev, [field.id]: null }));
                      }}
                      error={error}
                      aispaceColor={aispaceColor}
                    />
                  );
                }
                if (field.field_type === 'file') {
                  const fileList = Array.isArray(customFieldValues[field.id]) ? customFieldValues[field.id] : [];
                  return (
                    <CustomFileField
                      key={field.id}
                      field={field}
                      value={fileList}
                      onChange={(files) => {
                        setCustomFieldValues((prev) => ({ ...prev, [field.id]: files }));
                        if (error) setCustomFieldErrors((prev) => ({ ...prev, [field.id]: null }));
                      }}
                      error={error}
                      aispaceColor={aispaceColor}
                      onSharePoint={() => {
                        if (!newProject.agent) {
                          toast.error('Bitte wählen Sie zuerst einen Agenten aus');
                          return;
                        }
                        setSharepointCustomFieldId(field.id);
                        setShowSharePointBrowser(true);
                      }}
                    />
                  );
                }
                const value = customFieldValues[field.id] ?? '';
                const set = (v) => {
                  setCustomFieldValues((prev) => ({ ...prev, [field.id]: v }));
                  if (error) setCustomFieldErrors((prev) => ({ ...prev, [field.id]: null }));
                };
                const baseInputCls = `w-full px-4 py-3 text-[14px] border rounded-lg focus:outline-none ${error ? 'border-[#EF4444]' : 'border-[#E5E7EB]'}`;
                let opts = [];
                if (field.field_type === 'dropdown' && field.options) {
                  try { const p = JSON.parse(field.options); if (Array.isArray(p)) opts = p; } catch { /* ignore */ }
                }
                return (
                  <div key={field.id}>
                    <label className="block text-[14px] font-semibold text-[#111827] mb-2">
                      {(field.field_label === 'Projektname' || field.field_label === 'KI-Anfrage-Name') ? t('createProjectModal.projectNameLabel') : field.field_label}
                      {field.required && <span className="text-[#EF4444] ml-1">*</span>}
                    </label>
                    {field.field_type === 'textarea' ? (
                      <textarea
                        value={value}
                        onChange={(e) => set(e.target.value)}
                        rows={3}
                        className={baseInputCls + ' resize-none'}
                        onFocus={(e) => e.target.style.borderColor = aispaceColor}
                        onBlur={(e) => e.target.style.borderColor = error ? '#EF4444' : '#E5E7EB'}
                      />
                    ) : field.field_type === 'dropdown' ? (
                      <select
                        value={value}
                        onChange={(e) => set(e.target.value)}
                        className={baseInputCls + ' bg-white'}
                        onFocus={(e) => e.target.style.borderColor = aispaceColor}
                        onBlur={(e) => e.target.style.borderColor = error ? '#EF4444' : '#E5E7EB'}
                      >
                        <option value="">—</option>
                        {opts.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : (
                      <input
                        type={field.field_type === 'number' ? 'number' : (field.field_type === 'date' ? 'date' : 'text')}
                        value={value}
                        onChange={(e) => set(e.target.value)}
                        className={baseInputCls}
                        onFocus={(e) => e.target.style.borderColor = aispaceColor}
                        onBlur={(e) => e.target.style.borderColor = error ? '#EF4444' : '#E5E7EB'}
                      />
                    )}
                    {field.helper_text && !error && (
                      <p className="text-[12px] text-[#6B7280] mt-1">{field.helper_text}</p>
                    )}
                    {error && (
                      <p className="text-[12px] text-[#EF4444] mt-1">{error}</p>
                    )}
                  </div>
                );
              })}

              {/* Upload sections (file picker / SharePoint) — only shown once
                  loading is done AND the agent has no custom fields. */}
              {!customFieldsLoading && customFields.length === 0 && (
              <>
              {/* Upload Method */}
              <div>
                <label className="block text-[14px] font-semibold text-[#111827] mb-3">{t('createProjectModal.uploadMethodLabel')}</label>
                <div className="space-y-3">
                  {/* Hidden file input */}
                  <input
                    type="file"
                    ref={documentInputRef}
                    onChange={handleDocumentUpload}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.ppt,.pptx,.txt,.jpg,.jpeg,.png"
                    multiple
                    className="hidden"
                  />

                  {/* Upload from device */}
                  <button
                    onClick={() => {
                      setNewProject(prev => ({ ...prev, uploadMethod: 'device' }));
                      documentInputRef.current?.click();
                    }}
                    className="w-full flex items-center gap-4 p-4 rounded-xl transition-colors"
                    style={{
                      border: newProject.uploadMethod === 'device' ? `1.5px solid ${aispaceColor}` : '0.5px solid #E5E7EB',
                      backgroundColor: newProject.uploadMethod === 'device' ? `${aispaceColor}15` : 'transparent'
                    }}
                  >
                    <div className="w-10 h-10 flex items-center justify-center">
                      <Upload size={22} className="text-[#6B7280]" />
                    </div>
                    <div className="text-left">
                      <p className="text-[15px] font-semibold text-[#111827]">{t('createProjectModal.uploadFromDevice')}</p>
                      <p className="text-[13px] text-[#6B7280]">{t('createProjectModal.uploadFromDeviceHint')}</p>
                    </div>
                  </button>

                  {/* Connect SharePoint */}
                  <button
                    onClick={() => {
                      if (!newProject.agent) {
                        toast.error('Bitte wählen Sie zuerst einen Agenten aus');
                        return;
                      }
                      setNewProject(prev => ({ ...prev, uploadMethod: 'sharepoint' }));
                      setShowSharePointBrowser(true);
                    }}
                    className="w-full flex items-center gap-4 p-4 rounded-xl transition-colors"
                    style={{
                      border: newProject.uploadMethod === 'sharepoint' ? `1.5px solid ${aispaceColor}` : '0.5px solid #E5E7EB',
                      backgroundColor: newProject.uploadMethod === 'sharepoint' ? `${aispaceColor}15` : 'transparent'
                    }}
                  >
                    <div className="w-10 h-10 flex items-center justify-center">
                      <FolderOpen size={22} className="text-[#6B7280]" />
                    </div>
                    <div className="text-left">
                      <p className="text-[15px] font-semibold text-[#111827]">{t('createProjectModal.connectSharePoint')}</p>
                      <p className="text-[13px] text-[#6B7280]">{t('createProjectModal.connectSharePointHint')}</p>
                    </div>
                  </button>
                </div>
              </div>

              {/* Uploaded Files List - shown when files are uploaded */}
              {newProject.uploadedFiles.length > 0 && (
                <div className="space-y-2">
                  {newProject.uploadedFiles.map(file => (
                    <div key={file.id} className="flex items-center justify-between p-3 bg-[#F9FAFB] rounded-lg">
                      <div className="flex items-center gap-3">
                        {file.type?.startsWith('image/')
                          ? <ImageIcon size={20} className="text-[#6B7280]" />
                          : <FileText size={20} className="text-[#6B7280]" />}
                        <div>
                          <p className="text-[14px] font-medium text-[#374151] truncate max-w-[280px]">{file.name}</p>
                          <p className="text-[12px] text-[#9CA3AF]">{formatFileSize(file.size)}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => removeUploadedFile(file.id)}
                        className="p-1 hover:bg-[#FEE2E2] rounded transition-colors"
                      >
                        <X size={16} className="text-[#EF4444]" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* SharePoint imported files list */}
              {newProject.uploadMethod === 'sharepoint' && importedSharePointFiles.length > 0 && (
                <div className="space-y-3">
                  {/* Header */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5 text-blue-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                      </svg>
                      <span className="text-[14px] font-semibold text-[#111827]">SharePoint-Dateien ({importedSharePointFiles.filter(f => f.status === 'synced').length})</span>
                    </div>
                    <button
                      onClick={() => setShowSharePointBrowser(true)}
                      className="text-[13px] font-medium hover:opacity-80"
                      style={{ color: aispaceColor }}
                    >
                      + Weitere Dateien hinzufügen
                    </button>
                  </div>

                  {/* Successfully imported files */}
                  {importedSharePointFiles.filter(f => f.status === 'synced').length > 0 && (
                    <div className="space-y-2 max-h-[150px] overflow-y-auto thin-scrollbar">
                      {importedSharePointFiles
                        .filter(f => f.status === 'synced')
                        .map(file => (
                          <div key={file.id} className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                            <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                              <FileText size={16} className="text-green-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-[14px] font-medium text-[#374151] truncate">{file.name}</p>
                              <p className="text-[12px] text-green-600">{file.pageCount} Seiten • Aus SharePoint importiert</p>
                            </div>
                            <svg className="w-5 h-5 text-green-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        ))}
                    </div>
                  )}

                  {/* Failed files with error */}
                  {importedSharePointFiles.filter(f => f.status === 'error').length > 0 && (
                    <div className="space-y-2">
                      <p className="text-[12px] font-medium text-red-600">Import fehlgeschlagen:</p>
                      {importedSharePointFiles
                        .filter(f => f.status === 'error')
                        .map(file => (
                          <div key={file.id} className="flex items-center gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                            <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                              <AlertCircle size={16} className="text-red-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-[14px] font-medium text-[#374151] truncate">{file.name}</p>
                              <p className="text-[12px] text-red-600 truncate">{file.error}</p>
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              )}

              {/* SharePoint configured but no files yet */}
              {newProject.uploadMethod === 'sharepoint' && newProject.sharePointConfigured && importedSharePointFiles.length === 0 && (
                <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-[14px] text-blue-700">SharePoint verbunden - Wählen Sie Dateien zum Importieren</span>
                  </div>
                  <button
                    onClick={() => setShowSharePointBrowser(true)}
                    className="text-[13px] text-blue-700 hover:text-blue-800 underline"
                  >
                    Dateien durchsuchen
                  </button>
                </div>
              )}
              </>
              )}
              </>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 bg-[#F9FAFB] border-t border-[#E5E7EB] rounded-b-xl">
              <button
                onClick={closeCreateProjectModal}
                disabled={isCreatingProject}
                className="px-6 py-2.5 text-[14px] font-medium text-[#374151] bg-white border border-[#D1D5DB] rounded-lg hover:bg-[#F9FAFB] transition-colors disabled:opacity-50"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleAnalyze}
                disabled={!newProject.agent || (customFields.length === 0 && !newProject.projectName) || isCreatingProject}
                className="px-6 py-2.5 text-[14px] font-medium text-white rounded-lg transition-colors hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                style={{ backgroundColor: aispaceColor }}
              >
                {isCreatingProject ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {t('createProjectModal.creating')}
                  </>
                ) : (
                  t('createProjectModal.analyze')
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SharePoint File Browser - wrapped with higher z-index to appear on top of Create Project modal */}
      {showSharePointBrowser && (
        <div className="fixed inset-0 z-[300]">
          <SharePointFileBrowser
            isOpen={showSharePointBrowser}
            onClose={handleSharePointBrowserClose}
            organizationId={userData?.organization_id}
            taskId={newProject.agent}
            userOrganizationId={userData?.organization_id}
          />
        </div>
      )}

      <ShareModal
        isOpen={!!shareTarget}
        onClose={() => setShareTarget(null)}
        historyId={shareTarget?.id || ''}
        projectName={shareTarget?.name || ''}
        onInviteSent={fetchMyShares}
        currentUser={{
          name: userData?.user_fullname || userData?.user_email || localStorage.getItem('email'),
          email: userData?.user_email || localStorage.getItem('email'),
        }}
      />
    </div>
  );
};

export default HomeScreen
